import React from 'react';
import { postRequest, batchRequest, getRequest } from "../api/ApiService";
import Auth from "../routes/Auth";
import jstz from "jstz";
import moment from "moment-timezone";
import { logError } from "../services/utils";
import EventEmitter from "../services/event-emitter";
import { reactLocalStorage } from "reactjs-localstorage";

const AUTHORIZATION_ERROR =
  "You do not have authorization to submit %ROLE% decisions.";

class SubmitDecisions {
  submitDecisionsRequest(type) {
    return postRequest(`${process.env.REACT_APP_API_URL}teams/submit_${type}/`);
  }

  getUnacceptedDecisionsRequest(temporaryUser = null) {
    let apiURL = `${process.env.REACT_APP_API_URL}teams/unaccepted_decisions/`;
    if (temporaryUser) {
      apiURL = `${apiURL}?team_id=${temporaryUser.team_id}`;
    }

    return apiURL;
  }

  getSubmissionDateRequest(
    type,
    round_number = null,
    is_round_finished = false,
    temporaryUser = null,
  ) {
    const game_id = temporaryUser ? temporaryUser.game_id : Auth.getGameId();
    const base_url = `${process.env.REACT_APP_API_URL}games/${game_id}/journal/`;
    const literal = is_round_finished ? (temporaryUser ? "&" : "?") : "&";
    const additionalQuery = is_round_finished
      ? temporaryUser
        ? `?team_id=${temporaryUser.team_id}`
        : ""
      : temporaryUser
        ? `&team_id=${temporaryUser.team_id}`
        : "";

    let result_url =
      is_round_finished === true
        ? `${process.env.REACT_APP_API_URL}teams/submit_${type}/${additionalQuery}`
        : `${base_url}?path=/api/teams/submit_${type}/${additionalQuery}${literal}only_last=true`;
    if (round_number !== null && round_number !== false) {
      result_url =
        is_round_finished === true
          ? `${result_url}${literal}round_number=${round_number}`
          : `${result_url}&round_number=${round_number}`;
    }

    return result_url;
  }

  getSubmissionRequest(
    types,
    round_number = null,
    is_round_finished = false,
    temporaryUser = null,
  ) {
    let requests = [
      // eslint-disable-next-line
      getUnacceptedDecisionsRequest(temporaryUser)
    ];

    types.forEach(type => {
      let param = "";
      if (type === "pd") {
        param = "development";
      } else if (type === "pm") {
        param = "marketing";
      } else if (type === "op") {
        param = "operations";
      } else if (type === "fn") {
        param = "finance";
      }

      if (param !== "") {
        requests.push(
          // eslint-disable-next-line
          getSubmissionDateRequest(
            param,
            round_number,
            is_round_finished,
            temporaryUser,
          )
        );
      }
    });

    return requests;
  }

  handleSubmission(types, res, is_round_finished = false) {
    let data = {};

    types.forEach(type => {
      data[type] = {
        unaccepted: 0,
        date: "N/A"
      };
    });

    res.forEach((r, i) => {
      if (i === 0) {
        Object.keys(r).forEach(field => {
          if (types.includes(field.toLowerCase())) {
            data[field.toLowerCase()].unaccepted = r[field];
          }
        });
      } else {
        const timezone = jstz.determine();

        if (is_round_finished === true) {
          const type = types[i - 1];

          data[type].date =
            r.length > 0
              ? moment()
                .tz(timezone.name())
                .format("MM/DD hh:mmA")
              : "N/A";
          data[type].unaccepted = 0;
        } else {
          let date = "";

          if (!r[0] || r[0].requested_at === null) {
            date = "N/A";
          } else {
            date = moment(r[0].requested_at)
              .tz(timezone.name())
              .format("MM/DD hh:mmA");
          }

          const type = types[i - 1];
          data[type].date = date;
        }
      }
    });

    return data;
  }

  getSubmissionData(
    types,
    temporaryUser = null,
  ) {
    let requests = [
      // eslint-disable-next-line
      getUnacceptedDecisionsRequest(temporaryUser)
    ];

    types.forEach(type => {
      let param = "";
      if (type === "pd") {
        param = "development";
      } else if (type === "pm") {
        param = "marketing";
      } else if (type === "op") {
        param = "operations";
      } else if (type === "fn") {
        param = "finance";
      }

      if (param !== "") {
        requests.push(
          // eslint-disable-next-line
          getSubmissionDateRequest(param, null, undefined, temporaryUser)
        );
      }
    });

    const joinRequest = batchRequest(requests);

    return joinRequest
      .then(res => {
        let data = {};

        types.forEach(type => {
          data[type] = {
            unaccepted: 0,
            date: "N/A"
          };
        });

        res.forEach((r, i) => {
          const rq = r.body;

          if (i === 0) {
            Object.keys(rq).forEach(field => {
              if (types.includes(field.toLowerCase())) {
                data[field.toLowerCase()].unaccepted = rq[field];
              }
            });
          } else {
            let date = "";

            if (!rq[0] || rq[0].requested_at === null) {
              date = "N/A";
            } else {
              const timezone = jstz.determine();
              date = moment(rq[0].requested_at)
                .tz(timezone.name())
                .format("MM/DD hh:mmA");
            }

            const type = types[i - 1];
            data[type].date = date;
          }
        });

        return new Promise((resolve, reject) => {
          resolve(data);

          return null;
        });
      })
      .catch(err => {
        logError(err, "SubmitDecisions", "getSubmissionData");
      });
  }

  incrementUnacceptedDecisions(type) {
    let value =
      Number(reactLocalStorage.get(`${type}-unaccepted-decisions`, "0")) + 1;
    if (value === 0) {
      value = 1;
    }

    reactLocalStorage.set(`${type}-unaccepted-decisions`, value);
  }

  decrementUnacceptedDecisions(type) {
    let value =
      Number(reactLocalStorage.get(`${type}-unaccepted-decisions`, "0")) - 1;
    if (value === 0) {
      value = -1;
    }

    reactLocalStorage.set(`${type}-unaccepted-decisions`, value);
  }

  checkJournal(type, eventemitter, unaccepted_types, roundNumber, callback) {
    getRequest(`${process.env.REACT_APP_API_URL}games/${Auth.getGameId()}/journal/?round_number=${roundNumber}`)
      .then(res => {
        if (type === 'development') {
          let history = res.body.filter(i => (
            i.round_number === roundNumber && (
              i.path.startsWith('/api/team/products/') ||
              i.path.startsWith('/api/teams/submit_development/')
            )
          ));

          history.sort((a, b) => {
            const dateA = new Date(a.requested_at);
            const dateB = new Date(b.requested_at);

            return dateB - dateA;
          });

          if (history.length > 0 && history[0].path === '/api/teams/submit_development/' && (history[0].status_code === 200 || history[0].status_code === 201)) {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);

            if (unaccepted_types.includes(type)) {
              reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
            }
          }
        } else if (type === 'marketing') {
          const history = res.body.filter(i => (
            i.round_number === roundNumber && (
              i.path.startsWith('/api/team/price_forecasts/') ||
              i.path.startsWith('/api/team/promos/') ||
              i.path.startsWith('/api/team/sales/')
            )
          ));

          if (history.length > 0) {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);

            if (unaccepted_types.includes(type)) {
              reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
            }
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "info",
              (
                <div>
                  You haven't made a marketing decision this
                  round therefore there's no decision to post.
                </div>
              )
            ]);
          }
        } else if (type === 'operations') {
          let history = res.body.filter(i => (
            i.round_number === roundNumber && (
              i.path.startsWith('/api/team/factories/') ||
              i.path.startsWith('/api/team/machines/') ||
              i.path.startsWith('/api/team/product_manufactures/') ||
              i.path.startsWith('/api/teams/submit_operations/')
            )
          ));
          history = history.sort((a, b) => {
            const dateA = new Date(a.requested_at);
            const dateB = new Date(b.requested_at);

            return dateB - dateA;
          });

          if (history.length > 0 && history[0].path === '/api/teams/submit_operations/' && (history[0].status_code === 200 || history[0].status_code === 201)) {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);

            if (unaccepted_types.includes(type)) {
              reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
            }
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "info",
              (
                <div>
                  You haven't made an operation decision this
                  round therefore there's no decision to post.
                </div>
              )
            ]);
          }
        } else if (type === 'finance') {
          const history = res.body.filter(i => (
            i.round_number === roundNumber && (
              i.path.startsWith('/api/team/equities/') ||
              i.path.startsWith('/api/team/debts/') ||
              i.path.startsWith('/api/team/debt_repayments/') ||
              i.path.startsWith('/api/team/dividends_payment/') ||
              i.path.startsWith('/api/team/stock_repurchased/')
            )
          ));

          if (history.length > 0) {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);

            if (unaccepted_types.includes(type)) {
              reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
            }
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "info",
              (
                <div>
                  You haven't made a finance decision this
                  round therefore there's no decision to post.
                </div>
              )
            ]);
          }
        }

        if (callback) {
          callback();
        }
      })
      .catch(err => { });
  }

  handleSubmittedRequest(success, type, eventemitter, unaccepted_types, roundNumber, callback, errorMessage) {
    if (success === false) {
      // The backend uses a stable sentinel string when Finance hasn't
      // been posted yet — switch the user-facing message so they don't
      // assume it's a cash-balance problem.
      const isFinanceFirstError = typeof errorMessage === 'string'
        && errorMessage.toLowerCase().includes('finance decisions first');

      const message = isFinanceFirstError
        ? (
          <div>
            <b>Submit Finance Decision First</b><br />
            Your Head of Finance or CEO needs to post/submit finance decisions before you can post any other decisons.
          </div>
        )
        : (
          <div>
            You don't have enough cash to post these decisions. If your Finance manager recently raised capital, have them post their Finance decisions first so that cash counts toward your balance.
          </div>
        );

      eventemitter.trigger("submit-decisions-event", [
        "error",
        message,
      ]);

      if (callback) {
        callback();
      }

      return;
    }

    if (unaccepted_types.includes(type)) {
      reactLocalStorage.set(`${type}-unaccepted-decisions`, 0);
    }

    // Refresh data first, then show validation warnings using fresh team-decisions data
    if (callback) {
      callback(function showWarnings(decisionsData, meta) {
        const allPosted = !!(meta && meta.allPosted);

        // Copy used when this submit finished the round's decisions.
        // Kept in one place so every branch below (no-data path, per-type
        // warning path, plain success path) can swap it in when the
        // caller signals all four roles are posted with nothing pending.
        const allPostedMessage = (
          <div>
            <div style={{
              fontFamily: "'Mukta Vaani', Lato, sans-serif",
              fontSize: 18,
              lineHeight: '26px',
              fontWeight: 700,
              color: 'rgba(0, 27, 59, 0.94)',
              marginBottom: 12,
            }}>
              You're all set for this round.
            </div>
            <div style={{
              fontFamily: "'Mukta Vaani', Lato, sans-serif",
              fontSize: 16,
              lineHeight: '24px',
              fontWeight: 400,
              color: 'rgba(0, 27, 59, 0.75)',
            }}>
              All four department decisions are posted. Sit tight while the
              round runs and results come in.
              <br /><br />
              <b style={{ color: '#DC2626' }}>Heads up:</b> if anyone on the team edits a decision after this,
              that department flips back to unposted and the CEO will need
              to repost it before the deadline.
            </div>
          </div>
        );

        // Emits with 3rd arg = size. App.js passes it to DecisionsAlert,
        // which renders the celebratory large variant instead of the
        // routine toast.
        const emitAllPosted = () => eventemitter.trigger(
          "submit-decisions-event",
          ["success", allPostedMessage, "lg"]
        );

        if (!decisionsData) {
          if (allPosted) {
            emitAllPosted();
          } else {
            eventemitter.trigger("submit-decisions-event", [
              "success",
              (
                <div>
                  Your decisions were successfully submitted.
                  Your financials have been updated.
                </div>
              )
            ]);
          }
          return;
        }

        if (type === 'development') {
          const products = decisionsData.products || [];
          const months_available = products.reduce((prev, curr) => prev + (curr.months_available_to_produce || 0), 0);
          if (months_available === 0) {
            eventemitter.trigger("submit-decisions-event", [
              "warning",
              (
                <div>
                  Your financials have been updated, but there's a warning.<br />
                  <b style={{ color: '#ffb940' }}>Warning</b>: You do not have at least 1 product
                  that is set to be released this year. Will this impact your performance?
                </div>
              )
            ]);
            return;
          }
        } else if (type === 'marketing') {
          const priceForecast = decisionsData.price_forecasts || [];
          const promos = decisionsData.promos || [];
          const sales = decisionsData.sales || [];
          const hasPrice0 = priceForecast.filter(i => i.price === 0).length > 0;
          const spend = promos.reduce((prev, curr) => prev + curr.spend, 0);
          const total = sales.reduce((prev, curr) => prev + curr.total, 0);

          if (hasPrice0 || spend === 0 || total === 0) {
            eventemitter.trigger("submit-decisions-event", [
              "warning",
              (
                <div>
                  Your financials have been updated, but there's a warning.<br />
                  <b style={{ color: '#ffb940' }}>Warning</b>: You haven't made all of your
                  decisions. Please make sure you've made pricing, promo, and sales channel decisions.
                </div>
              )
            ]);
            return;
          }
        } else if (type === 'operations') {
          const inventoryBalances = decisionsData.inventory_balances || [];
          // Skip the warning if the caller's role doesn't include
          // inventory_balances in the response. e.g., the CEO page
          // refreshes with role='ceo' after any submit, and that
          // payload omits inventory_balances — treating that as
          // "empty inventory" would fire a false-positive low-inventory
          // warning even when the OP page shows plenty of units.
          if (inventoryBalances.length === 0) {
            if (allPosted) {
              emitAllPosted();
            } else {
              eventemitter.trigger("submit-decisions-event", [
                "success",
                <div>Your decisions were successfully submitted. Your financials have been updated.</div>
              ]);
            }
            return;
          }
          const available_for_sale = inventoryBalances.reduce((prev, curr) => prev + (curr.available_for_sale || 0), 0);

          if (available_for_sale <= 100000) {
            eventemitter.trigger("submit-decisions-event", [
              "warning",
              (
                <div>
                  Your financials have been updated, but there's a warning.<br />
                  <b style={{ color: '#ffb940' }}>Warning</b>: You have low inventory. Please
                  consider producing more units.
                </div>
              )
            ]);
            return;
          }
        }

        // No warnings — show success (celebration variant if this was
        // the final of four posts, plain toast otherwise).
        if (allPosted) {
          emitAllPosted();
        } else {
          eventemitter.trigger("submit-decisions-event", [
            "success",
            (
              <div>
                Your decisions were successfully submitted.
                Your financials have been updated.
              </div>
            )
          ]);
        }
      });
    }
  }

  submitDecisions(type, roundNumber, callback) {
    const eventemitter = EventEmitter.getInstance();
    const role = Auth.getRole();

    if (type === "development" && role !== "CEO" && role !== "PD") {
      eventemitter.trigger("app-show-error", [
        AUTHORIZATION_ERROR.replace("%ROLE%", "product development")
      ]);
      callback();
      return;
    }

    if (type === "marketing" && role !== "CEO" && role !== "PM") {
      eventemitter.trigger("app-show-error", [
        AUTHORIZATION_ERROR.replace("%ROLE%", "product marketing")
      ]);
      callback();
      return;
    }

    if (type === "operations" && role !== "CEO" && role !== "OP") {
      eventemitter.trigger("app-show-error", [
        AUTHORIZATION_ERROR.replace("%ROLE%", "operations")
      ]);
      callback();
      return;
    }

    if (type === "finance" && role !== "CEO" && role !== "FI") {
      eventemitter.trigger("app-show-error", [
        AUTHORIZATION_ERROR.replace("%ROLE%", "finance")
      ]);
      callback();
      return;
    }

    const unaccepted_types = [
      "development",
      "marketing",
      "finance",
      "operations"
    ];

    // eslint-disable-next-line
    submitDecisionsRequest(type)
      .then(res => {
        const body = res.body || {};
        const { success } = body;
        handleSubmittedRequest(
          success, type, eventemitter, unaccepted_types, roundNumber, callback,
          body.error
        );
      })
      .catch(err => {
        if (err.response && err.response.status === 400) {
          // The 400 body carries `{ success: false, error: "..." }` —
          // grab the error string so we can swap in the Finance-first
          // alert when that's what the backend reported.
          const body = (err.response && err.response.body) || {};
          handleSubmittedRequest(
            false, type, eventemitter, unaccepted_types, roundNumber, callback,
            body.error
          );
        } else {
          logError(err, "SubmitDecisions", "submitDecisions");
        }
      });
  }
}

const submitDecisionsRequest = SubmitDecisions.prototype.submitDecisionsRequest;
const getUnacceptedDecisionsRequest =
  SubmitDecisions.prototype.getUnacceptedDecisionsRequest;
const getSubmissionDateRequest =
  SubmitDecisions.prototype.getSubmissionDateRequest;
const handleSubmittedRequest = SubmitDecisions.prototype.handleSubmittedRequest;

export const submitDecisions = SubmitDecisions.prototype.submitDecisions;
export const getSubmissionData = SubmitDecisions.prototype.getSubmissionData;
export const getSubmissionRequest =
  SubmitDecisions.prototype.getSubmissionRequest;
export const handleSubmission = SubmitDecisions.prototype.handleSubmission;
export const incrementUnacceptedDecisions =
  SubmitDecisions.prototype.incrementUnacceptedDecisions;
export const decrementUnacceptedDecisions =
  SubmitDecisions.prototype.decrementUnacceptedDecisions;
export const checkJournal = SubmitDecisions.prototype.checkJournal;



// WEBPACK FOOTER //
// ./src/api/SubmitDecisions.js